/**
 * Care Team React Query Hooks
 * BP-06-03: Care Team & Goals Frontend
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { careTeamApi } from '@/features/care-team/api/careTeamApi';
import type {
  CareTeamMember,
  CareTeamMemberType,
  CareTeamByCategory,
  CreateCareTeamMemberRequest,
  UpdateCareTeamMemberRequest,
  CareTeamFilters,
  CareTeamExportOptions,
  ProviderSearchParams,
} from '@/features/care-team/types/careTeamTypes';

// ============================================================================
// Query Keys
// ============================================================================

export const careTeamKeys = {
  all: ['care-team'] as const,
  lists: () => [...careTeamKeys.all, 'list'] as const,
  list: (familyId: string, filters?: CareTeamFilters) => 
    [...careTeamKeys.lists(), familyId, filters] as const,
  byCategory: (familyId: string) => 
    [...careTeamKeys.all, 'by-category', familyId] as const,
  byChild: (childId: string) => 
    [...careTeamKeys.all, 'by-child', childId] as const,
  details: () => [...careTeamKeys.all, 'detail'] as const,
  detail: (memberId: string) => [...careTeamKeys.details(), memberId] as const,
  types: () => [...careTeamKeys.all, 'types'] as const,
  providers: (params: ProviderSearchParams) => 
    [...careTeamKeys.all, 'providers', params] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch all care team members for a family
 */
export function useCareTeam(familyId: string, filters?: CareTeamFilters) {
  return useQuery({
    queryKey: careTeamKeys.list(familyId, filters),
    queryFn: () => careTeamApi.getAll(familyId, filters),
    enabled: !!familyId,
  });
}

/**
 * Hook to fetch a single care team member
 */
export function useCareTeamMember(memberId: string | null) {
  return useQuery({
    queryKey: careTeamKeys.detail(memberId!),
    queryFn: () => careTeamApi.getById(memberId!),
    enabled: !!memberId,
  });
}

/**
 * Hook to fetch care team grouped by category
 */
export function useCareTeamByCategory(familyId: string) {
  return useQuery({
    queryKey: careTeamKeys.byCategory(familyId),
    queryFn: () => careTeamApi.getByCategory(familyId),
    enabled: !!familyId,
  });
}

/**
 * Hook to fetch care team for a specific child
 */
export function useCareTeamByChild(childId: string) {
  return useQuery({
    queryKey: careTeamKeys.byChild(childId),
    queryFn: () => careTeamApi.getByChild(childId),
    enabled: !!childId,
  });
}

/**
 * Hook to fetch all care team member types
 */
export function useCareTeamMemberTypes() {
  return useQuery({
    queryKey: careTeamKeys.types(),
    queryFn: () => careTeamApi.getMemberTypes(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}

/**
 * Hook to search provider directory
 */
export function useProviderSearch(params: ProviderSearchParams, enabled = true) {
  return useQuery({
    queryKey: careTeamKeys.providers(params),
    queryFn: () => careTeamApi.searchProviders(params),
    enabled: enabled && (!!params.query || !!params.category || !!params.memberTypeId),
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new care team member
 */
export function useCreateCareTeamMember(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCareTeamMemberRequest) =>
      careTeamApi.create(familyId, data),
    onSuccess: (newMember) => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: careTeamKeys.lists() });
      queryClient.invalidateQueries({ queryKey: careTeamKeys.byCategory(familyId) });
      
      // If child-specific, invalidate child query
      if (newMember.childId) {
        queryClient.invalidateQueries({ 
          queryKey: careTeamKeys.byChild(newMember.childId) 
        });
      }
    },
  });
}

/**
 * Hook to update a care team member
 */
export function useUpdateCareTeamMember(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, data }: { memberId: string; data: UpdateCareTeamMemberRequest }) =>
      careTeamApi.update(memberId, data),
    onSuccess: (updatedMember, { memberId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        careTeamKeys.detail(memberId),
        updatedMember
      );
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: careTeamKeys.lists() });
      queryClient.invalidateQueries({ queryKey: careTeamKeys.byCategory(familyId) });
      
      // If child-specific, invalidate child query
      if (updatedMember.childId) {
        queryClient.invalidateQueries({ 
          queryKey: careTeamKeys.byChild(updatedMember.childId) 
        });
      }
    },
  });
}

/**
 * Hook to delete a care team member
 */
export function useDeleteCareTeamMember(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => careTeamApi.delete(memberId),
    onSuccess: (_, memberId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: careTeamKeys.detail(memberId) });
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: careTeamKeys.lists() });
      queryClient.invalidateQueries({ queryKey: careTeamKeys.byCategory(familyId) });
    },
  });
}

/**
 * Hook to set a care team member as primary
 */
export function useSetPrimaryCareTeamMember(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => careTeamApi.setPrimary(memberId),
    onSuccess: (updatedMember, memberId) => {
      // Update the detail cache
      queryClient.setQueryData(
        careTeamKeys.detail(memberId),
        updatedMember
      );
      
      // Invalidate list queries to refresh primary flags
      queryClient.invalidateQueries({ queryKey: careTeamKeys.lists() });
      queryClient.invalidateQueries({ queryKey: careTeamKeys.byCategory(familyId) });
    },
  });
}

/**
 * Hook to export care team
 */
export function useExportCareTeam(familyId: string) {
  return useMutation({
    mutationFn: (options: CareTeamExportOptions) =>
      careTeamApi.export(familyId, options),
    onSuccess: (blob, options) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `care-team-${new Date().toISOString().split('T')[0]}.${options.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ============================================================================
// Combined Hook for Page State
// ============================================================================

interface UseCareTeamPageOptions {
  familyId: string;
  filters?: CareTeamFilters;
}

export function useCareTeamPage({ familyId, filters }: UseCareTeamPageOptions) {
  const careTeamQuery = useCareTeam(familyId, filters);
  const byCategoryQuery = useCareTeamByCategory(familyId);
  const memberTypesQuery = useCareTeamMemberTypes();
  
  const createMutation = useCreateCareTeamMember(familyId);
  const updateMutation = useUpdateCareTeamMember(familyId);
  const deleteMutation = useDeleteCareTeamMember(familyId);
  const setPrimaryMutation = useSetPrimaryCareTeamMember(familyId);
  const exportMutation = useExportCareTeam(familyId);

  return {
    // Queries
    careTeam: careTeamQuery.data?.items ?? [],
    total: careTeamQuery.data?.total ?? 0,
    byCategory: byCategoryQuery.data ?? [],
    memberTypes: memberTypesQuery.data ?? [],
    
    // Loading states
    isLoading: careTeamQuery.isLoading,
    isByCategoryLoading: byCategoryQuery.isLoading,
    isMemberTypesLoading: memberTypesQuery.isLoading,
    
    // Error states
    error: careTeamQuery.error,
    
    // Mutations
    createMember: createMutation.mutateAsync,
    updateMember: updateMutation.mutateAsync,
    deleteMember: deleteMutation.mutateAsync,
    setPrimary: setPrimaryMutation.mutateAsync,
    exportCareTeam: exportMutation.mutateAsync,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Refetch
    refetch: careTeamQuery.refetch,
  };
}

// Care Team Types Hook
export function useCareTeamTypes() {
  return {
    data: [
      { id: 'physician', name: 'Physician', category: 'medical' },
      { id: 'therapist', name: 'Therapist', category: 'therapy' },
      { id: 'specialist', name: 'Specialist', category: 'medical' },
      { id: 'nurse', name: 'Nurse', category: 'medical' },
      { id: 'aide', name: 'Aide', category: 'support' },
      { id: 'teacher', name: 'Teacher', category: 'education' },
      { id: 'counselor', name: 'Counselor', category: 'therapy' },
    ],
    isLoading: false,
    error: null,
  };
}


export function useSetPrimaryProvider(familyId: string) {
  return useSetPrimaryCareTeamMember(familyId);
}

export function useToggleMemberActive(familyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId, active }: { memberId: string; active: boolean }) => {
      // Implement toggle active API call
      return { memberId, active };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careTeamKeys.all });
    },
  });
}

export function useGenerateVCard(familyId: string) {
  return useMutation({
    mutationFn: async (memberId: string) => {
      // Implement vCard generation
      return { memberId };
    },
  });
}



export function useCareTeamStats(familyId: string) {
  return useQuery({
    queryKey: [...careTeamKeys.all, 'stats', familyId],
    queryFn: async () => {
      // Return mock stats or implement API call
      return {
        total: 0,
        byCategory: {},
        primary: null,
      };
    },
  });
}

export function useExportCareTeamPdf(familyId: string) {
  return useMutation({
    mutationFn: async () => {
      return careTeamApi.export ? careTeamApi.export(familyId, { format: 'pdf' }) : null;
    },
  });
}

export function useExportCareTeamCsv(familyId: string) {
  return useMutation({
    mutationFn: async () => {
      return careTeamApi.export ? careTeamApi.export(familyId, { format: 'csv' }) : null;
    },
  });
}
